/**
 * SSE客户端封装
 * 用于处理服务器发送事件(Server-Sent Events)的请求与响应
 * 默认使用多例模式，每次调用createSseConnection都会创建一个新的连接实例
 */

import { nanoid } from '../../packages/utils/src';
import { getServiceBaseURL } from './service';

// 定义SSE连接选项的接口
interface SseConnectionOptions {
  onMessage?: (data: any) => void;
  onOpen?: (event: Event) => void;
  onError?: (error: Event | Error) => void;
  onComplete?: () => void;
  timeout?: number;
  maxRetry?: number;
  shouldRetry?: (error: Event | Error) => boolean;
  statusCode?: number;
}
const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

// 存储所有活跃的SSE连接实例
const connections: Map<string, SseClient> = new Map();

export class SseClient {
  id: string;
  eventSource: EventSource | null;
  retryCount: number;
  maxRetry: number;
  retryInterval: number;
  options: SseConnectionOptions;
  isConnected: boolean;
  connectionTimeout: NodeJS.Timeout | null;
  lastUrl: string;

  constructor() {
    this.id = `sse_${nanoid()}`; // 自动生成唯一ID
    this.eventSource = null;
    this.retryCount = 0;
    this.maxRetry = 300;
    this.retryInterval = 2000; // 2秒重试间隔
    this.options = {};
    this.isConnected = false;
    this.connectionTimeout = null;
    this.lastUrl = ''; // 保存最后一次连接的URL
  }

  /**
   * 建立SSE连接
   * @param {String} url SSE请求地址
   * @param {Object} options 配置选项
   * @returns {Promise<SseClient>} 返回Promise对象
   */
  connect(url: string, options: SseConnectionOptions = {}): Promise<SseClient> {
    // 保存URL以便重试
    this.lastUrl = url;

    // 合并选项
    this.options = {
      onMessage: () => {},
      onOpen: () => {},
      onError: () => {},
      onComplete: () => {},
      timeout: 15000,
      maxRetry: this.maxRetry,
      shouldRetry: () => true,
      statusCode: 88,
      ...options
    };

    return new Promise((resolve, reject) => {
      // 关闭已有连接
      this.close();

      try {
        // 设置连接超时
        this.connectionTimeout = setTimeout(() => {
          if (!this.isConnected) {
            const error = new Error('SSE连接超时');
            this.handleError(error);
            reject(error);
          }
        }, this.options.timeout);

        // 创建EventSource对象
        this.eventSource = new EventSource(url);

        // 连接打开事件
        this.eventSource.onopen = (event: Event) => {
          this.isConnected = true;
          if (this.connectionTimeout) clearTimeout(this.connectionTimeout);
          this.options.onOpen?.(event);
          resolve(this);
        };

        // 接收消息事件
        this.eventSource.onmessage = (event: MessageEvent) => {
          try {
            // const data = JSON.parse(event.data);

            // // 检查是否是最终消息
            // if (data.status === this.options.statusCode) {
            //   this.options.onComplete?.();
            //   this.close();
            //   return;
            // }

            // 处理消息数据
            this.options.onMessage?.(event.data);
          } catch (error) {
            console.error('SSE消息解析错误:', error);
            if (error instanceof Error) {
              this.handleError(error);
            }
          }
        };

        // 错误处理事件
        this.eventSource.onerror = (error: Event) => {
          this.handleError(error);
          if (this.retryCount >= (this.options.maxRetry ?? this.maxRetry)) {
            reject(error);
          }
        };
      } catch (error) {
        console.error('创建SSE连接失败:', error);
        if (error instanceof Error) {
          this.handleError(error);
          reject(error);
        }
      }
    });
  }

  /**
   * 处理错误
   * @private
   * @param {Error | Event} error 错误对象
   */
  private handleError(error: Error | Event) {
    this.options.onError?.(error);

    // 清除超时计时器
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // 检查是否应该重试
    if (this.retryCount < (this.options.maxRetry ?? this.maxRetry) && this.options.shouldRetry?.(error)) {
      this.retryCount += 1;
      console.log(`SSE连接失败，${this.retryInterval / 1000}秒后进行第${this.retryCount}次重试`);

      // 关闭当前连接
      this.close();

      // 延迟重试
      setTimeout(() => {
        this.connect(this.lastUrl, this.options).catch(err => console.error('SSE重试连接失败:', err));
      }, this.retryInterval);
    } else {
      this.close();
    }
  }

  /**
   * 关闭SSE连接
   */
  close() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.isConnected = false;

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // 从连接池中移除
    connections.delete(this.id);
  }

  /**
   * 检查连接是否打开
   * @returns {Boolean} 连接状态
   */
  isOpen() {
    return this.isConnected && this.eventSource && this.eventSource.readyState === 1;
  }
}

/**
 * 创建新的SSE连接
 * @param {String} url SSE请求地址
 * @param {Object} options 配置选项
 * @returns {Promise<SseClient>} 返回Promise，解析为SSE客户端实例
 */
export const createSseConnection = (url: string, options: SseConnectionOptions = {}): Promise<SseClient> => {
  const client = new SseClient();

  // 添加到连接池
  connections.set(client.id, client);
  let finalUrl = url;
  if (!finalUrl.startsWith('http')) {
    // eslint-disable-next-line no-param-reassign
    finalUrl = `${baseURL}${url}`;
  }
  return client.connect(finalUrl, options);
};

/**
 * 关闭指定的SSE连接
 * @param {SseClient} client SSE客户端实例
 */
export const closeSseConnection = (client: SseClient) => {
  if (client && typeof client.close === 'function') {
    client.close();
  }
};

/**
 * 关闭所有SSE连接
 */
export const closeAllSseConnections = () => {
  connections.forEach(client => client.close());
  connections.clear();
};

/**
 * 检查指定的SSE连接是否打开
 * @param {SseClient} client SSE客户端实例
 * @returns {Boolean} 连接状态
 */
export const isSseConnectionOpen = (client: SseClient) => {
  return client && typeof client.isOpen === 'function' && client.isOpen();
};

/**
 * 获取当前所有活跃的SSE连接
 * @returns {Array<SseClient>} 连接实例数组
 */
export const getAllSseConnections = (): SseClient[] => {
  return Array.from(connections.values());
};

/**
 * 获取活跃连接数量
 * @returns {Number} 连接数量
 */
export const getSseConnectionCount = () => {
  return connections.size;
};

export default {
  createSseConnection,
  closeSseConnection,
  closeAllSseConnections,
  isSseConnectionOpen,
  getAllSseConnections,
  getSseConnectionCount
};
